import { FormControlLabel } from '@mui/material';
import { type ColDef } from 'ag-grid-community';
import { BaseTableList } from 'components/AgGrid/AgGrid';
import { ListWrapper } from 'components/AgGrid/CellRenderer/ListView.styled';
import StyledSwitchButton from 'components/Buttons/SwitchButton';
import { FlexBox } from 'components/Containers/FlexBox';
import * as React from 'react';
import TabletHandler from 'handlers/tablet.handler';
import { useAppSelector } from 'store/hook';
import {
  selectAllTablets,
  selectUnassociatedTablets,
} from 'store/tablet.slice';
import { type Tablet } from 'types/tablet.types';
import { isDarkTheme } from 'utils/theme';
import { ThemePalette } from 'mui.theme';
import { isExternalUser } from 'utils/validators/role.validation';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { TabletInfoModal } from './modal/TabletInfoModal';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

enum OpenModal {
  None,
  TabletInfo,
}

export const TabletList = () => {
  const { t } = useTranslation();
  const [selectedTabletId, setSelectedTabletId] = React.useState('');
  const [modalOpen, setModalOpen] = React.useState(OpenModal.None);
  const [showUnassociated, setShowUnassociated] = React.useState(false);

  const allTablets = useAppSelector(selectAllTablets);
  const unassociatedTablets = useAppSelector(selectUnassociatedTablets);
  const { customer, role, isAuthenticated, accessToken } = useAppSelector(
    (state) => state.authReducer
  );
  const enableDarkTheme = isDarkTheme(customer.theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const isDisabled = isExternalUser(role);
  const { timezoneBasedOnDisplayMode, preferredTimeDisplayMode } =
    useTimeDisplayMode();
  const tabletHandler = new TabletHandler();

  React.useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    void tabletHandler.getAll();
  }, [isAuthenticated, accessToken]);

  const formatDate = (val: any) => {
    if (!val) return 'N/A';
    return convertUtcToDisplayTime(
      val,
      preferredTimeDisplayMode,
      timezoneBasedOnDisplayMode
    );
  };

  const tabletList = React.useMemo(() => {
    const associated = allTablets.filter((t) => t.bumperId);
    return [...associated, ...unassociatedTablets];
  }, [allTablets, unassociatedTablets]);

  const selectedTablet = tabletList.find(
    (t) => t.tabletId === selectedTabletId
  );

  const columnDefs = React.useMemo<ColDef[]>(
    () => [
      {
        field: 'tabletId',
        headerName: t('tablet_id'),
        minWidth: 180,
        filter: 'agTextColumnFilter',
        sortable: true,
        cellRenderer: ({ value, data }: any) => (
          <span
            style={{
              color: '#38bdf8',
              cursor: isDisabled ? 'default' : 'pointer',
              fontWeight: 600,
            }}
            onClick={() => {
              if (isDisabled) return;
              setSelectedTabletId(data.tabletId);
              setModalOpen(OpenModal.TabletInfo);
            }}
          >
            {value}
          </span>
        ),
      },
      {
        field: 'deviceId',
        headerName: t('device_column'),
        minWidth: 160,
        filter: 'agTextColumnFilter',
        sortable: true,
        valueFormatter: ({ value }: any) => value || '—',
      },
      {
        field: 'bumperId',
        headerName: t('bumper_id_10'),
        minWidth: 130,
        filter: 'agTextColumnFilter',
        sortable: true,
      },
      {
        field: 'appVersion',
        headerName: t('app_version'),
        minWidth: 120,
        valueFormatter: ({ value }: any) => value || '—',
      },
      {
        field: 'customerName',
        headerName: t('customer'),
        minWidth: 130,
        filter: 'agTextColumnFilter',
        valueFormatter: ({ value }: any) => value || '—',
      },
      {
        field: 'lastSync',
        headerName: t('last_sync_column'),
        minWidth: 170,
        valueFormatter: ({ value }: any) => formatDate(value),
      },
      {
        field: 'status',
        headerName: t('cdp_step_status'),
        minWidth: 140,
        filter: 'agTextColumnFilter',
        cellRenderer: ({ value }: any) => {
          const color =
            value === 'active'
              ? '#22c55e'
              : value === 'inactive'
              ? '#f59e0b'
              : '#ef4444';
          return (
            <span
              style={{ color, fontWeight: 600, textTransform: 'capitalize' }}
            >
              {value || '—'}
            </span>
          );
        },
      },
    ],
    [isDisabled]
  );

  return (
    <>
      {modalOpen === OpenModal.TabletInfo && selectedTablet && (
        <TabletInfoModal
          key="tablet-info-modal"
          tabletData={selectedTablet}
          title={t('edit_tablet')}
          open
          handleAbort={() => {
            setModalOpen(OpenModal.None);
          }}
        />
      )}

      <FlexBox
        sx={{
          display: 'flex',
          marginRight: '2rem',
          justifyContent: 'flex-end',
        }}
      >
        <FormControlLabel
          control={<StyledSwitchButton />}
          label={t('show_unlinked_tablets')}
          labelPlacement="start"
          checked={showUnassociated}
          onChange={() => {
            setShowUnassociated((v) => !v);
          }}
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
        />
      </FlexBox>

      <ListWrapper>
        <BaseTableList
          rowData={tabletList}
          columnDefs={columnDefs}
          isExternalFilterPresent={!showUnassociated}
          filter={(node) => !!(node.data as Tablet)?.bumperId}
        />
      </ListWrapper>
    </>
  );
};
